import React, { useEffect, useRef } from "react";
import { FiSearch } from "react-icons/fi";
import { BsCalendarDate } from "react-icons/bs";
import { AiFillCaretDown } from "react-icons/ai";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../../components/calendartheme.css";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { toast } from "react-toastify";

const LeadManagementContent = ({
  statusOptions,
  selectedStatus,
  isStatusDropdownOpen,
  setIsStatusDropdownOpen,
  handleStatusChange,
  searchTerm,
  handleSearchChange,
  isDatePickerOpen,
  setIsDatePickerOpen,
  startDate,
  endDate,
  setDateRange,
  selectAll,
  handleSelectAll,
  selectedLeads,
  loading,
  error,
  filteredLeads,
  handleSelectLead,
  getStatusStyle,
}) => {
  const datePickerRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // ✅ Export selected or all leads to Excel inside ZIP
  const exportToExcelAndZip = async () => {
    const leadsToExport =
      selectedLeads.length > 0
        ? filteredLeads.filter((l) => selectedLeads.includes(l.id))
        : filteredLeads;

    if (!leadsToExport || leadsToExport.length === 0) {
      toast.warn("No leads to export.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const dataForExcel = leadsToExport.map((l) => ({
      "Date & Time": `${l.applicationDate || "N/A"} ${l.time || ""}`,
      "Applicant Name": l.applicantName || "N/A",
      "Phone Number": l.phoneNumber || "N/A",
      "Loan Amount": l.loanAmount || "N/A",
      "Loan Type": l.loanType || "N/A",
      "Recent Update": l.recentUpdate || "N/A",
      Status: l.status || "N/A",
      Commission: l.commission || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    XLSX.utils.book_append_sheet(wb, ws, "Leads");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const zip = new JSZip();
    zip.file("leads.xlsx", excelBuffer);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "leads.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ✅ Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsDatePickerOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    };

    if (isDatePickerOpen || isStatusDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDatePickerOpen, setIsDatePickerOpen, isStatusDropdownOpen, setIsStatusDropdownOpen]);

  return (
    <>
      <div className="flex flex-wrap lg:flex-nowrap items-center gap-4 mb-4">
        {/* Search */}
        <div className="relative flex items-center bg-transparent border border-[#333333] rounded-full w-full sm:w-80">
          <FiSearch className="absolute left-4 w-[24px] text-[#F3F3F3]" />
          <input
            type="text"
            placeholder="Search by Loan Amount or Type"
            className="bg-transparent text-white rounded-full pl-10 py-2.5 leading-normal focus:outline-none w-full"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        {/* Calendar + Status + Download ZIP */}
        <div className="flex justify-between lg:justify-normal w-full px-4 lg:w-2/3">
          {/* Calendar */}
          <div className="relative border border-[#333333] rounded-md" ref={datePickerRef}>
            <button
              className="flex items-center bg-transparent rounded-md px-2 sm:px-3 py-2 focus:outline-none text-sm sm:text-base"
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            >
              <BsCalendarDate className="mr-1 sm:mr-2 text-[#FFCA20] text-sm sm:text-base" />
              <span className="hidden sm:inline">
                {startDate && endDate
                  ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                  : "Choose Dates"}
              </span>
              <span className="sm:hidden">
                {startDate && endDate
                  ? `${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                  : "Dates"}
              </span>
            </button>
            {isDatePickerOpen && (
              <div className="absolute z-10 mt-2 left-0 bg-[#1e1e1e] rounded-lg p-2 border border-[#333333]">
                <Calendar
                  selectRange={true}
                  onChange={(update) => {
                    if (update[0] && update[1]) {
                      setDateRange(update);
                      setIsDatePickerOpen(false);
                    }
                  }}
                  value={[startDate, endDate]}
                  calendarType="gregory"
                  prev2Label={null}
                  next2Label={null}
                />
              </div>
            )}
          </div>

          {/* Status Dropdown */}
          <div
            className="relative border border-[#333333] rounded-md lg:ml-5"
            ref={statusDropdownRef}
          >
            <button
              className="flex items-center bg-transparent rounded-md px-3 py-2 focus:outline-none"
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
            >
              Status <AiFillCaretDown className="ml-1 text-[#FFCA20]" />
            </button>
            {isStatusDropdownOpen && (
              <>
                {/* Mobile */}
                <div className="sm:hidden absolute z-10 mt-1 right-0 w-48 bg-[#1e1e1e] rounded-md shadow-lg">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2c2c2c] first:rounded-t-md last:rounded-b-md"
                      onClick={() => handleStatusChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {/* Desktop */}
                <div className="hidden sm:block absolute z-10 mt-1 w-48 bg-[#1e1e1e] rounded-md shadow-lg">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2c2c2c]"
                      onClick={() => handleStatusChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Download ZIP Button */}
          <button
            onClick={exportToExcelAndZip}
            className="ml-3 px-4 py-2 rounded-md bg-yellow-400 text-black font-bold"
          >
            Download as ZIP
          </button>
        </div>
      </div>

      {/* Bulk Select */}
      {/* <div className="mb-2">
        <label className="inline-flex items-center text-sm text-gray-500">
          <input
            type="checkbox"
            className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
            checked={selectAll}
            onChange={handleSelectAll}
          />
          <span className="ml-2">Select to enable bulk options</span>
        </label>

        {selectedLeads.length > 0 && (
          <div className="mt-2 flex gap-2">
            <span className="text-sm text-gray-400">{selectedLeads.length} selected</span>
            <button className="text-sm text-[#FFCA20] hover:underline">Bulk Action</button>
          </div>
        )}
      </div> */}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-4">{error}</div>
      ) : (
        <div className="overflow-x-auto scrollbar-hide">
          <table className="min-w-full border-collapse border border-[#333333] rounded-lg overflow-hidden">
            <thead className="bg-[#1e1e1e]">
              <tr>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  <input
                    type="checkbox"
                    className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Applicant Name
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Loan Amount
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Recent Update
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Status
                </th>
                <th className="border border-[#333333] px-4 py-3 text-right text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Commission (2.5%)
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#1e1e1e] border border-[#333333] rounded-lg">
              {filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-4 text-center text-[#F3F3F3]">
                    No leads found
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead) => (
                  <tr key={lead.id}>
                    <td className="border border-[#333333] px-4 py-3">
                      <input
                        type="checkbox"
                        className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                      />
                    </td>
                    <td className="border border-[#333333] px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex flex-col">
                        <span>{lead.applicationDate}</span>
                        <span className="text-white">{lead.time}</span>
                      </div>
                    </td>
                    <td className="border border-[#333333] px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex flex-col">
                        <span className="text-white">{lead.applicantName || "N/A"}</span>
                        <span className="text-yellow-500 text-xs">
                          <p>Affiliate Application</p>
                        </span>
                      </div>
                    </td>
                    <td className="border border-[#333333] px-4 py-3 whitespace-nowrap text-sm text-[#F3F3F3]">
                      {lead.phoneNumber || "N/A"}
                    </td>
                    <td className="border border-[#333333] px-4 py-3 whitespace-nowrap text-sm text-[#F3F3F3]">
                      <div className="flex flex-col">
                        <span>{lead.loanAmount}</span>
                        <span className="text-[#F3F3F3] text-xs">{lead.loanType}</span>
                      </div>
                    </td>
                    <td className="border border-[#333333] px-4 py-3 whitespace-nowrap text-sm text-[#F3F3F3]">
                      {lead.recentUpdate}
                    </td>
                    <td className="border border-[#333333] px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-4 py-1 rounded-full ${getStatusStyle(lead.status)}`}>
                        {lead.status.charAt(0).toUpperCase() +
                          lead.status.slice(1).replace("-", " ")}
                      </span>
                    </td>
                    <td className="border border-[#333333] px-4 py-3 whitespace-nowrap text-sm text-center text-green-400">
                      {lead.commission}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default LeadManagementContent;
